import {
  Alert,
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { PaymentMethodDiscountValidationResult } from "../../../services/paymentMethodDiscountService";
import Price from "../../common/Price";

import { CheckoutItem } from "./types";

interface OrderSummaryProps {
  items: CheckoutItem[];
  totalAmount: number;
  shippingCost: number;
  tax: number;
  finalTotal: number;
  isCalculatingShipping: boolean;
  hasItemsNeedingShipping: boolean;
  onItemSelectionChange?: (itemId: string, selected: boolean) => void;
  showSelection?: boolean;
  paymentMethodDiscount?: PaymentMethodDiscountValidationResult | null; // Payment method discount
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  totalAmount: _totalAmount,
  shippingCost: _shippingCost,
  tax: _tax,
  finalTotal: _finalTotal,
  isCalculatingShipping,
  hasItemsNeedingShipping,
  onItemSelectionChange,
  showSelection = false,
  paymentMethodDiscount: _paymentMethodDiscount,
}) => {
  const { t: myTranslate } = useTranslation();
  const { data: exchangeRate } = useExchangeRate();

  const handleItemSelectionChange = (itemId: string, selected: boolean) => {
    if (onItemSelectionChange) {
      onItemSelectionChange(itemId, selected);
    }
  };

  const renderShippingInfo = (item: CheckoutItem) => {
    const parts: React.ReactNode[] = [];

    // Seller's internal shipping fee
    if (item.sellerShippingFee && item.sellerShippingFee > 0) {
      parts.push(
        <Typography
          key="seller-ship"
          variant="caption"
          className="notranslate"
          translate="no"
          sx={{ color: amazonColors.text.secondary }}
        >
          {myTranslate("checkout.sellerShipping")}: +{" "}
          <Price value={item.sellerShippingFee} tjsAmount={convertUsdToTjs(item.sellerShippingFee, exchangeRate ?? null)} />
        </Typography>
      );
    }

    // Cargo shipping (US→TJ)
    if (item.shippingCalculated && item.shippingCost !== undefined) {
      parts.push(
        <Typography
          key="cargo-ship"
          variant="caption"
          className="notranslate"
          translate="no"
          sx={{ color: amazonColors.text.secondary }}
        >
          {myTranslate("checkout.shipping")}: +{" "}
          <Price value={item.shippingCost} tjsAmount={convertUsdToTjs(item.shippingCost ?? 0, exchangeRate ?? null)} /> {myTranslate("checkout.each")}
        </Typography>
      );
    } else {
      parts.push(
        <Box key="calc-ship" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {isCalculatingShipping && (
            <CircularProgress size={12} sx={{ color: amazonColors.orange }} />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            className="notranslate"
            translate="no"
          >
            {myTranslate("checkout.calculatingShipping")}
          </Typography>
        </Box>
      );
    }

    return <>{parts}</>;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {hasItemsNeedingShipping && !isCalculatingShipping && (
        <Alert
          severity="warning"
          sx={{
            borderRadius: 2,
            "& .MuiAlert-icon": { color: amazonColors.orange },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 500 }}
            className="notranslate"
            translate="no"
          >
            {myTranslate("checkout.someItemsNeedShipping")}
          </Typography>
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #E0E0E0",
          boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
          backgroundColor: "#ffffff",
          overflow: "hidden",
        }}
      >
        {items.map((item, index) => (
          <Box
            key={item.id}
            sx={{ opacity: showSelection && item.selected !== true ? 0.6 : 1 }}
          >
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                gap: { xs: 1.25, sm: 2 },
                flexDirection: "row",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                {showSelection && (
                  <Checkbox
                    size="small"
                    checked={item.selected === true}
                    onChange={(e) =>
                      handleItemSelectionChange(item.id, e.target.checked)
                    }
                    sx={{
                      p: 0.5,
                      color: amazonColors.orange,
                      "&.Mui-checked": { color: amazonColors.orange },
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: { xs: 72, sm: 84 },
                    height: { xs: 72, sm: 84 },
                    flexShrink: 0,
                    borderRadius: 2,
                    border: "1px solid #F5F5F5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    translate="no"
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Price
                      value={item.price * item.quantity + (item.sellerShippingFee || 0)}
                      disableCentsHeuristic
                      tjsAmount={convertUsdToTjs(item.price * item.quantity + (item.sellerShippingFee || 0), exchangeRate ?? null)}
                    />
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        className="notranslate"
                        translate="no"
                      >
                        {myTranslate("checkout.quantity")}: {" "}
                        <strong>{item.quantity}</strong>
                      </Typography>
                      <Typography
                        translate="no"
                        variant="caption"
                        color="text.secondary"
                        className="notranslate"
                      >
                        • <Price value={item.price} tjsAmount={convertUsdToTjs(item.price, exchangeRate ?? null)} />{" "}
                        {myTranslate("checkout.each")}
                      </Typography>
                    </Box>

                    {renderShippingInfo(item)}
                  </Box>
                </Box>
              </Box>
            </Box>
            {index < items.length - 1 && <Divider />}
          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default OrderSummary;
